import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiAlertOutline } from '@mdi/js'
import { useCan, useCanOrgWide } from '../../contexts/PermissionsContext'
import { useLocale } from '../../contexts/LocaleContext'
import { pickLabel, useExpenseCategories } from '../../hooks/useExpenseStatement'
import {
  percent,
  useChargeAllocationRules,
  useCreateChargeOverride,
  useDeleteChargeOverride,
  useUpdateChargeOverride,
  type ChargeAllocationRule,
  type ChargeAllocationRules,
  type RecoverableValue,
} from '../../hooks/useRecoverableCharges'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { AppIcon } from '../ui/icon'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'

type Entry = ChargeAllocationRules['rules'][number]

const SELECT_CLASS = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60'
const ANY = '__any__'

/** A rule's key as the row attribute the screen and its tests read. */
const keyOf = (e: { cost_category?: string | null; regime?: string | null; under_contract?: boolean | null }) =>
  `${e.cost_category ?? '*'}/${e.regime ?? '*'}/${e.under_contract == null ? '*' : String(e.under_contract)}`

interface Draft {
  uid?: string
  cost_category: string
  regime: string
  under_contract: string
  recoverable: RecoverableValue
  ratio: string
  note: string
  /** The pack's value on the same key, for the legal warning. */
  packValue?: RecoverableValue
  packBasis?: string | null
}

/**
 * "Recoverable charges" on the business rules page (spec 2026-10-25-09): the
 * jurisdiction's shipped rules with their legal basis, and the organization's
 * overrides, edited by an organization-wide finance.write holder.
 */
export function RecoverableChargesSection({ org }: { org: string }) {
  const { t } = useTranslation('compliance')
  const { locale } = useLocale()
  const canRead = useCan('finance.read')
  // The override routes need finance.write held organization-wide.
  const canWrite = useCanOrgWide('finance.write')
  const [jurisdiction, setJurisdiction] = useState<string | undefined>(undefined)
  const { data } = useChargeAllocationRules(org, jurisdiction, canRead)
  const { data: taxonomy } = useExpenseCategories(org)
  const create = useCreateChargeOverride(org)
  const update = useUpdateChargeOverride(org)
  const remove = useDeleteChargeOverride(org)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!canRead || !data) return null
  const jurisdictions = [...new Set([data.jurisdiction, ...data.releases.map((r) => r.jurisdiction)])].sort()

  const categoryLabel = (code?: string | null) => {
    if (!code) return t('recoverable_rules.any')
    const c = taxonomy?.categories.find((x) => x.code === code)
    return c ? pickLabel(c.label, locale) : code
  }
  const valueLabel = (r: Pick<ChargeAllocationRule, 'recoverable' | 'recoverable_ratio'>) =>
    t(`recoverable_rules.value_${r.recoverable}`, { percent: percent(r.recoverable_ratio) })
  const explanationOf = (r: ChargeAllocationRule) => {
    const e = r.explanation
    if (!e) return ''
    return locale.startsWith('fr') ? e.fr || e.en : e.en || e.fr
  }
  const departsFromLaw = (value: RecoverableValue, packValue?: RecoverableValue) =>
    data.jurisdiction === 'FR' && packValue === 'no' && value !== 'no'

  const openFor = (e: Entry | null) => {
    setError(null)
    const o = e?.override
    setDraft({
      uid: o?.uid,
      cost_category: e?.cost_category ?? ANY,
      regime: e?.regime ?? ANY,
      under_contract: e?.under_contract == null ? ANY : String(e.under_contract),
      recoverable: o?.recoverable ?? (e?.pack?.recoverable === 'yes' ? 'no' : 'yes'),
      ratio: o?.recoverable_ratio != null ? String(percent(o.recoverable_ratio)) : '',
      note: o?.note ?? '',
      packValue: e?.pack?.recoverable,
      packBasis: e?.pack?.legal_basis,
    })
  }

  const save = async () => {
    if (!draft) return
    const pct = Number(draft.ratio)
    if (!draft.note.trim() || (draft.recoverable === 'partial' && !(pct > 0 && pct < 100))) return
    const ratio = draft.recoverable === 'partial' ? pct / 100 : null
    try {
      if (draft.uid) {
        await update.mutateAsync(draft.uid, { recoverable: draft.recoverable, recoverable_ratio: ratio, note: draft.note })
      } else {
        await create.mutateAsync({
          jurisdiction: data.jurisdiction,
          cost_category: draft.cost_category === ANY ? null : draft.cost_category,
          regime: draft.regime === ANY ? null : (draft.regime as 'current' | 'works'),
          under_contract: draft.under_contract === ANY ? null : draft.under_contract === 'true',
          recoverable: draft.recoverable,
          recoverable_ratio: ratio,
          note: draft.note,
        })
      }
      setDraft(null)
    } catch {
      setError(t('recoverable_rules.error_generic'))
    }
  }

  // The pack row a new override's key falls on, to show what it departs from.
  const packFor = (d: Draft) => data.rules.find((e) => keyOf(e) === keyOf({
    cost_category: d.cost_category === ANY ? null : d.cost_category,
    regime: d.regime === ANY ? null : d.regime,
    under_contract: d.under_contract === ANY ? null : d.under_contract === 'true',
  }))?.pack
  const draftPack = draft && !draft.uid ? packFor(draft) : undefined
  const draftPackValue = draft?.uid ? draft.packValue : draftPack?.recoverable
  const draftPackBasis = draft?.uid ? draft.packBasis : draftPack?.legal_basis

  return (
    <section className="mt-8" data-testid="recoverable-rules-section">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('recoverable_rules.title')}</h2>
      <p className="text-sm text-muted-foreground mb-3">{t('recoverable_rules.subtitle')}</p>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-48">
          <Label htmlFor="recoverable-rules-jurisdiction" className="text-xs">{t('recoverable_rules.jurisdiction')}</Label>
          <select
            id="recoverable-rules-jurisdiction"
            className={SELECT_CLASS}
            value={data.jurisdiction}
            onChange={(e) => setJurisdiction(e.target.value)}
            data-testid="recoverable-rules-jurisdiction-select"
          >
            {jurisdictions.map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
        {canWrite && data.release && (
          <Button variant="outline" size="sm" onClick={() => openFor(null)} data-testid="recoverable-rule-add-override">
            {t('recoverable_rules.add_override')}
          </Button>
        )}
      </div>

      {!data.release ? (
        <p className="text-sm text-muted-foreground border rounded-lg px-4 py-3" data-testid="recoverable-rules-not-configured">
          {t('recoverable_rules.not_configured')}
        </p>
      ) : (
        <>
          {!data.release.validated && (
            <p className="mb-3 flex items-start gap-1.5 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-foreground" data-testid="recoverable-rules-indicative">
              <AppIcon path={mdiAlertOutline} size="xs" className="mt-0.5 shrink-0 text-warning" />
              {t('recoverable_rules.indicative')}
            </p>
          )}
          <div className="border rounded-lg divide-y">
            {data.rules.map((e) => {
              const rule = (e.override ?? e.pack)!
              const legalWarning = e.override && departsFromLaw(e.override.recoverable, e.pack?.recoverable)
              return (
                <div key={rule.uid} className="flex flex-wrap items-start gap-3 px-4 py-2.5" data-testid={`recoverable-rule-row-${rule.uid}`} data-rule-key={keyOf(e)}>
                  <div className="flex-1 min-w-[14rem] space-y-0.5">
                    <div className="text-sm font-medium">
                      {categoryLabel(e.cost_category)}
                      <span className="font-normal text-muted-foreground">
                        {' · '}{e.regime ? t(`recoverable_rules.regime_${e.regime}`) : t('recoverable_rules.any')}
                        {e.under_contract != null && ` · ${t(`recoverable_rules.under_contract_${e.under_contract}`)}`}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {rule.legal_basis && (rule.source_url
                        ? <a href={rule.source_url} target="_blank" rel="noreferrer" className="underline">{rule.legal_basis}</a>
                        : rule.legal_basis)}
                      {e.override && e.pack && ` · ${t('recoverable_rules.departs_from', { basis: valueLabel(e.pack) })}`}
                    </div>
                    {explanationOf(rule) && <div className="text-xs text-muted-foreground">{explanationOf(rule)}</div>}
                    {e.override?.note && <div className="text-xs italic text-muted-foreground">{e.override.note}</div>}
                    {legalWarning && (
                      <div className="text-xs text-foreground" data-testid={`recoverable-rule-legal-warning-${rule.uid}`}>
                        {t('recoverable_rules.legal_warning')}
                      </div>
                    )}
                  </div>
                  <Badge variant={rule.recoverable === 'no' ? 'secondary' : 'default'} className="text-xs font-normal" data-testid={`recoverable-rule-value-${rule.uid}`}>
                    {valueLabel(rule)}
                  </Badge>
                  <Badge variant="outline" className="text-[11px] font-normal" data-testid={`recoverable-rule-origin-${rule.uid}`}>
                    {e.override
                      ? t('recoverable_rules.origin_organization')
                      : t('recoverable_rules.origin_release', { version: data.release!.version })}
                  </Badge>
                  {canWrite && (e.override ? (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openFor(e)} data-testid={`recoverable-rule-override-edit-${rule.uid}`}>
                        {t('recoverable_rules.edit')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove.mutateAsync(rule.uid)} disabled={remove.isPending} data-testid={`recoverable-rule-override-remove-${rule.uid}`}>
                        {t('recoverable_rules.remove')}
                      </Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => openFor(e)} data-testid={`recoverable-rule-override-${rule.uid}`}>
                      {t('recoverable_rules.override')}
                    </Button>
                  ))}
                </div>
              )
            })}
          </div>
        </>
      )}

      <Dialog open={!!draft} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent data-testid="recoverable-rule-override-dialog">
          <DialogHeader>
            <DialogTitle>{t('recoverable_rules.dialog_title')}</DialogTitle>
            <DialogDescription>{t('recoverable_rules.dialog_description')}</DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-xs">{t('recoverable_rules.field_category')}</Label>
                  <select className={SELECT_CLASS} disabled={!!draft.uid} value={draft.cost_category}
                    onChange={(e) => setDraft({ ...draft, cost_category: e.target.value })} data-testid="recoverable-rule-override-category">
                    <option value={ANY}>{t('recoverable_rules.any')}</option>
                    {taxonomy?.categories.map((c) => <option key={c.code} value={c.code}>{pickLabel(c.label, locale)}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">{t('recoverable_rules.field_regime')}</Label>
                  <select className={SELECT_CLASS} disabled={!!draft.uid} value={draft.regime}
                    onChange={(e) => setDraft({ ...draft, regime: e.target.value })} data-testid="recoverable-rule-override-regime">
                    <option value={ANY}>{t('recoverable_rules.any')}</option>
                    <option value="current">{t('recoverable_rules.regime_current')}</option>
                    <option value="works">{t('recoverable_rules.regime_works')}</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">{t('recoverable_rules.field_contract')}</Label>
                  <select className={SELECT_CLASS} disabled={!!draft.uid} value={draft.under_contract}
                    onChange={(e) => setDraft({ ...draft, under_contract: e.target.value })} data-testid="recoverable-rule-override-contract">
                    <option value={ANY}>{t('recoverable_rules.any')}</option>
                    <option value="true">{t('recoverable_rules.contract_yes')}</option>
                    <option value="false">{t('recoverable_rules.contract_no')}</option>
                  </select>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="text-xs">{t('recoverable_rules.field_value')}</Label>
                  <select className={SELECT_CLASS} value={draft.recoverable}
                    onChange={(e) => setDraft({ ...draft, recoverable: e.target.value as RecoverableValue })} data-testid="recoverable-rule-override-value">
                    <option value="yes">{t('recoverable_rules.value_yes')}</option>
                    <option value="partial">{t('recoverable_rules.value_partial', { percent: '…' })}</option>
                    <option value="no">{t('recoverable_rules.value_no')}</option>
                  </select>
                </div>
                {draft.recoverable === 'partial' && (
                  <div>
                    <Label className="text-xs">{t('recoverable_rules.field_ratio')}</Label>
                    <Input type="number" min={1} max={99} value={draft.ratio}
                      onChange={(e) => setDraft({ ...draft, ratio: e.target.value })} data-testid="recoverable-rule-override-ratio" />
                  </div>
                )}
              </div>
              {draftPackBasis && (
                <p className="text-xs text-muted-foreground">{t('recoverable_rules.departs_from', { basis: draftPackBasis })}</p>
              )}
              {departsFromLaw(draft.recoverable, draftPackValue) && (
                <p className="flex items-start gap-1.5 text-xs text-foreground" data-testid="recoverable-rule-override-legal-warning">
                  <AppIcon path={mdiAlertOutline} size="xs" className="mt-0.5 shrink-0 text-warning" />
                  {t('recoverable_rules.legal_warning')}
                </p>
              )}
              <div>
                <Label className="text-xs">{t('recoverable_rules.field_note')}</Label>
                <Textarea rows={2} value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} data-testid="recoverable-rule-override-note" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="recoverable-rule-override-error">{error}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDraft(null)} data-testid="recoverable-rule-override-cancel">{t('recoverable_rules.cancel')}</Button>
            <Button onClick={save} disabled={!draft?.note.trim() || create.isPending || update.isPending} data-testid="recoverable-rule-override-save">
              {t('recoverable_rules.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
